'use client'

import { useAuth } from '@clerk/nextjs'
import { useSearchParams } from 'next/navigation'

import { useState } from 'react'
import { useAsync } from 'react-async-hook'

import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isNil from 'lodash/fp/isNil'

import type { Apps } from '../enums'
import type { ChildrenProps, ExtendedUser } from '../types'

import { DEFAULT_PUBLIC_PAGES } from '../constants'
import { UserContext } from '../contexts/User'
import { usePathname } from '../helpers/translations'
import useApi from '../hooks/useApi'

type Props<T extends ExtendedUser> = {
  app?: 'auto' | Apps
  publicPages?: Array<string>
  retry?: boolean
  user?: T | null
} & ChildrenProps
export default function UserProvider<T extends ExtendedUser>({
  app = 'auto',
  children,
  publicPages = DEFAULT_PUBLIC_PAGES,
  retry = true,
  user: providedUser = null,
}: Props<T>) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const endpoints = useApi()

  const { userId: clerkUserId } = useAuth()

  const authToken =
    (searchParams.get('auth_token') || searchParams.get('authToken')) ??
    undefined

  const wasProvided =
    !isEmpty(providedUser) &&
    (isNil(providedUser.authId) || providedUser.authId === clerkUserId)

  const [isLoading, setIsLoading] = useState(!wasProvided)
  const [user, setUser] = useState<T | null>(
    wasProvided ? (providedUser ?? null) : null,
  )

  const asyncUser = useAsync(
    async () => {
      setIsLoading(true)

      let loadedUser: T | null = null
      try {
        loadedUser = await endpoints.users.get<T>()({
          app,
          authToken,
          pathname,
          publicPages,
          retry,
        })

        setUser(loadedUser)
      } catch {
        setUser(null)
      } finally {
        setIsLoading(false)
      }

      return loadedUser
    },
    [clerkUserId, app],
    {
      executeOnMount: !wasProvided,
      setLoading: (state) => ({ ...state, loading: isEmpty(state.result) }),
    },
  )

  const reload = async ({ force = false } = {}) => {
    if ((isLoading || asyncUser.loading) && !force) {
      return asyncUser.currentPromise
    }

    return asyncUser.execute()
  }

  const set = (userToSet: ExtendedUser | null) => {
    setIsLoading(false)
    setUser(userToSet as T | null)

    if (typeof window !== 'undefined' && isFunction(window.DD_LOGS?.setUser)) {
      window.DD_LOGS.setUser(userToSet as ExtendedUser)
    }
  }

  return (
    <UserContext.Provider
      value={{
        clerkUserId,
        isLoading: isLoading && asyncUser.loading,
        isSilentlyLoading: isLoading && !asyncUser.loading,
        refresh: reload,
        reload,
        set,
        user,
        wasProvided,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
